import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { Bell, AlertTriangle, ClipboardCheck, CheckCheck, X } from "lucide-react";

const POLL_INTERVAL_MS = 30000;
const HEARTBEAT_INTERVAL_MS = 60000;

// Authenticated-only widget combining:
//   • A 60s presence heartbeat (keeps the user shown "online" in TeamHub)
//   • A 30s notification poll (new escalations / tasks / status changes)
//   • A bell icon + drawer for the unified inbox
// Pop-up toast fires once per never-before-seen notification.
export default function NotificationBell({ user }) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  // Stable across polls — used to detect newly-arrived notifs and toast them.
  const seenIds = useRef(new Set());
  const firstLoadDone = useRef(false);

  const fetchNotifs = useCallback(async () => {
    try {
      const r = await api.get("/inbox");
      const list = r.data?.notifications || [];
      setUnread(r.data?.unread_count || 0);
      setItems(list);
      // Toast for newly-arrived notifs — but skip the very first load,
      // otherwise it'd spam on page reload.
      if (firstLoadDone.current) {
        list.forEach(n => {
          if (!seenIds.current.has(n.notif_id) && !n.read) {
            toast(n.title, {
              description: n.subtitle || n.body || "",
              action: { label: "View", onClick: () => navigate(n.link || "/team-hub") },
            });
          }
          seenIds.current.add(n.notif_id);
        });
      } else {
        list.forEach(n => seenIds.current.add(n.notif_id));
        firstLoadDone.current = true;
      }
    } catch (err) {
      // Silent — polling shouldn't pollute the UI with errors.
    }
  }, [navigate]);

  // Presence heartbeat — keeps the dot on the user's avatar green.
  // Fires on mount + every 60s while the tab is open. Pauses when the
  // page is hidden to avoid noise (the next focus/visibility change
  // re-triggers it).
  useEffect(() => {
    if (!user) return;
    let interval = null;
    const beat = () => {
      api.post("/team-hub/presence?status=online").catch(() => {});
    };
    const start = () => {
      beat();
      interval = setInterval(beat, HEARTBEAT_INTERVAL_MS);
    };
    const stop = () => { if (interval) { clearInterval(interval); interval = null; } };
    const onVis = () => {
      if (document.hidden) stop(); else start();
    };
    if (!document.hidden) start();
    document.addEventListener("visibilitychange", onVis);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [user]);

  // Notification poll.
  useEffect(() => {
    if (!user) return;
    fetchNotifs();
    const id = setInterval(fetchNotifs, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [user, fetchNotifs]);

  const markAllRead = async () => {
    try {
      await api.post("/inbox/read-all");
      setUnread(0);
      setItems(prev => prev.map(n => ({ ...n, read: true })));
    } catch { /* ignore */ }
  };

  const openItem = async (n) => {
    if (!n.read) {
      try { await api.post("/inbox/read", { notif_ids: [n.notif_id] }); } catch { /* ignore */ }
      setItems(prev => prev.map(x => x.notif_id === n.notif_id ? { ...x, read: true } : x));
      setUnread(u => Math.max(0, u - 1));
    }
    setOpen(false);
    navigate(n.link || "/team-hub");
  };

  if (!user) return null;

  return (
    <>
      <button data-testid="notification-bell"
        onClick={() => setOpen(true)}
        title="Notifications"
        className="relative w-10 h-10 rounded-full hover:bg-slate-100 flex items-center justify-center transition-colors">
        <Bell className="w-5 h-5 text-slate-700" strokeWidth={1.8} />
        {unread > 0 && (
          <span data-testid="notification-bell-count"
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center ring-2 ring-white">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-start justify-end bg-black/30" onClick={() => setOpen(false)}
          data-testid="notification-drawer">
          <div className="bg-white w-full max-w-md h-full shadow-xl flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="border-b border-slate-200 px-5 py-4 flex items-center justify-between">
              <div>
                <h2 className="font-['Outfit'] text-lg font-semibold text-slate-900">Notifications</h2>
                <p className="text-[11px] text-slate-500">{unread > 0 ? `${unread} unread` : "All caught up"}</p>
              </div>
              <div className="flex items-center gap-1">
                {unread > 0 && (
                  <button onClick={markAllRead} data-testid="notification-mark-all-read"
                    className="text-xs font-medium text-slate-600 hover:text-slate-900 px-3 h-8 rounded-full hover:bg-slate-100 flex items-center gap-1">
                    <CheckCheck className="w-3.5 h-3.5" /> Mark all read
                  </button>
                )}
                <button onClick={() => setOpen(false)}
                  className="w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto">
              {items.length === 0 ? (
                <div className="text-center py-16 px-6">
                  <Bell className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-sm text-slate-500">No notifications</p>
                  <p className="text-xs text-slate-400 mt-1">When something needs your attention, it'll show up here.</p>
                </div>
              ) : items.map(n => (
                <NotificationRow key={n.notif_id} notif={n} onClick={() => openItem(n)} />
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function NotificationRow({ notif, onClick }) {
  const Icon = notif.type === "task" ? ClipboardCheck : AlertTriangle;
  const tone = notif.severity === "critical" || notif.severity === "high"
    ? "bg-rose-50 text-rose-700"
    : notif.severity === "low"
    ? "bg-slate-50 text-slate-600"
    : "bg-amber-50 text-amber-700";
  return (
    <button onClick={onClick}
      data-testid={`notification-row-${notif.notif_id}`}
      className={`w-full text-left flex items-start gap-3 px-5 py-3 border-b border-slate-100 hover:bg-[#FAFAFA] transition-colors ${
        !notif.read ? "bg-[#FFF8E7]/40" : ""
      }`}>
      {!notif.read && <span className="w-2 h-2 rounded-full bg-rose-500 mt-2 flex-shrink-0" />}
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${tone}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className={`text-sm truncate ${!notif.read ? "font-semibold text-slate-900" : "text-slate-700"}`}>{notif.title}</p>
          {notif.kind === "reported" && (
            <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">You reported</span>
          )}
        </div>
        {notif.subtitle && <p className="text-[11px] text-slate-500 mt-0.5 truncate">{notif.subtitle}</p>}
        {notif.body && <p className="text-xs text-slate-600 mt-1 line-clamp-2">{notif.body}</p>}
        <p className="text-[10px] text-slate-400 mt-1">
          {notif.created_at ? new Date(notif.created_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : ""}
        </p>
      </div>
    </button>
  );
}
